import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { arrayMove } from 'react-sortable-hoc';

import Text from 'relements/build/esm/input/text';
import Button from 'relements/build/esm/ui/button';
import Accordion from 'relements/build/esm/ui/accordion';

import { isProduction } from '@src/utils';
import { PERMISSION_LEVELS } from '@src/const';
import { userHasPermission } from '@src/actions/_utils';

import TaskHeader from './TaskHeader';
import TaskBody from './TaskBody';
import './Tasks.scss';
import { DEFAULTS } from '../../../const';

export default function Tasks({
  meta = {},
  value,
  onChange,
  onTopLevelChange,
  onTransfer,
}) {
  const { tasks } = value;
  const { taskboxMessage, allTasks } = meta;
  const [rowOpen, setRowOpen] = useState(-1);

  const editable =
    !isProduction() &&
    userHasPermission(PERMISSION_LEVELS.BUSINESS_TASK_EDIT_TRANSFER);

  const createTask = () => {
    const newTask = {
      id: Math.floor(Math.random() * 10000),
      ...DEFAULTS.TASK,
    };
    const newAllTasks = [newTask].concat(meta.allTasks);
    const newTasks = {
      ...value.tasks,
      [newTask.id]: {
        image_url: '',
        active: false,
      },
    };

    onTopLevelChange('allTasks', newAllTasks);
    setTimeout(() => {
      onChange({ ...value, tasks: newTasks });
    }, 0);
  };

  const handleChange = (key) => (valueForKey) => {
    const newValue = { ...value, [key]: valueForKey };
    onChange(newValue);
  };

  const handleTopChange = (key) => (valueForKey) => {
    onTopLevelChange(key, valueForKey);
  };

  const handleInnerChange = (key, i, existingValue, isTopLevel) => (
    valueForKey
  ) => {
    const newValue = existingValue;
    newValue[i] = valueForKey;
    return isTopLevel
      ? handleTopChange(key)(newValue)
      : handleChange(key)(newValue);
  };

  const handleSort = ({ oldIndex, newIndex }) => {
    const allTasksMutable = allTasks.asMutable();
    const orderedData = arrayMove(allTasksMutable, oldIndex, newIndex);
    onTopLevelChange('allTasks', orderedData);
  };

  const handleTransfer = (taskId) => (e) => {
    e.stopPropagation();
    onTransfer(taskId, e);
  };

  const renderTaskRow = (task, i) => {
    const activeClassName = rowOpen === i ? 'active' : '';
    return (
      <Accordion.Item.Sortable
        active={rowOpen === i}
        index={i}
        key={task.id}
        className={`business-tasks-list-item ${activeClassName}`}
        onChange={() => setRowOpen(rowOpen === i ? -1 : i)}
        renderHeader={({ ExpandIcon, ReorderIcon }) => (
          <TaskHeader
            id={task.id}
            title={task.name}
            message={task.message}
            value={tasks[task.id] || {}}
            sortableHandle={<ReorderIcon />}
            expandHandle={<ExpandIcon />}
            onChange={handleInnerChange('tasks', task.id, { ...tasks })}
            onTransfer={handleTransfer(task.id)}
            disabled={!editable}
          />
        )}
      >
        <TaskBody
          value={task}
          onChange={handleInnerChange('allTasks', i, [...allTasks], true)}
          meta={meta}
        />
      </Accordion.Item.Sortable>
    );
  };

  return (
    <div className="business-tasks">
      <div className="business-tasks-title">Menu Configuration</div>
      <div className="business-tasks-row">
        <Text
          label="Menu Header Message"
          placeholder="Menu Header Message"
          disabled={!editable}
          value={taskboxMessage}
          onChange={handleTopChange('taskboxMessage')}
        />
        {editable ? (
          <Button onClick={createTask} type={Button.TYPES.SECONDARY}>
            + Add Menu Options
          </Button>
        ) : null}
      </div>
      <div className="business-tasks-list">
        <div className="business-tasks-list-header">
          <div className="business-tasks-list-header-item">Menu Option(s)</div>
          <div className="business-tasks-list-header-item">User Messages</div>
          <div className="business-tasks-list-header-item">Menu Id</div>
        </div>
        <Accordion.Sortable
          useDragHandle
          lockAxis="y"
          helperClass="sortableElement"
          className="business-tasks-list-items"
          onSortEnd={handleSort}
        >
          {allTasks.map(renderTaskRow)}
        </Accordion.Sortable>
      </div>
    </div>
  );
}

Tasks.propTypes = {
  meta: PropTypes.object,
  onChange: PropTypes.func,
  onTopLevelChange: PropTypes.func,
  onTransfer: PropTypes.func,
  value: PropTypes.object,
};

Tasks.defaultProps = {
  meta: {},
  onChange: () => {},
  onTopLevelChange: () => {},
  onTransfer: () => {},
  value: {},
};
